import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import {
  EstablishmentDTO,
  TpvBoleta,
  TpvBuyDTO,
  TpvDetailDTO,
  TpvDetailOperation,
  TPVOptionOperations,
  TpvQueryResponse,
  TpvRefundDTO,
  TpvResponseDTO,
  TPVTariffDTO,
  TPVTypeSelected,
} from '../model/revolving-model';
import { RevolvingApi } from './revolving-api.service';

@Injectable()
export class RevolvingStore {
  private operations = [
    { id: TPVOptionOperations.liquidations, value: 'queries.combos.liquidations' },
    { id: TPVOptionOperations.purchase, value: 'queries.combos.purchases' },
    { id: TPVOptionOperations.refund, value: 'queries.combos.refunds' },
  ];

  private queryTypes = [
    { id: TPVTypeSelected.group, value: 'queries.group' },
    { id: TPVTypeSelected.prescriptor, value: 'queries.prescriber' },
    { id: TPVTypeSelected.establishment, value: 'queries.establishment' },
  ];

  constructor(private revolvingApi: RevolvingApi, private datePipe: DatePipe) {}

  getQueryTypes() {
    return this.queryTypes;
  }

  public loadOperations(): Promise<CommonDataDTO[]> {
    return Promise.resolve(this.operations);
  }

  public sendPurchase(purchasesDataForm: FormGroup, fractionalOperationForm: FormGroup): Promise<TpvResponseDTO> {
    const request: TpvBuyDTO = {
      amount: purchasesDataForm.get('purchaseImport')?.value?.slice(0, -1).trim(),
      contractNumber: purchasesDataForm.get('contractNum')?.value,
      establishment: purchasesDataForm.get('establishment')?.value,
      nif: purchasesDataForm.get('nifNie')?.value,
      rate: fractionalOperationForm.get('tariffCode')?.value,
      monthlyPayment:
        fractionalOperationForm.get('monthlyPayments')?.value !== ''
          ? fractionalOperationForm.get('monthlyPayments')?.value
          : 0,
      description: fractionalOperationForm.get('description')?.value,
    };
    return new Promise<any>((resolve, reject) => {
      this.revolvingApi
        .postTpvPurchase(request)
        .toPromise()
        .then(
          (data) => resolve(data),
          (error) => reject(error)
        );
    });
  }

  public sendRefund(refundDataForm: FormGroup): Promise<TpvResponseDTO> {
    const request: TpvRefundDTO = {
      establishment: refundDataForm.get('establishment')?.value,
      contractNumber: refundDataForm.get('contractNum')?.value,
      authorization: refundDataForm.get('authorizationNum')?.value,
      amount: refundDataForm.get('refundImport')?.value.replace('€', '').trim(),
      nif: refundDataForm.get('nifNie')?.value,
      description: refundDataForm.get('description')?.value,
    };

    return new Promise<any>((resolve, reject) => {
      this.revolvingApi
        .postTpvRefund(request)
        .toPromise()
        .then(
          (data) => resolve(data),
          (error) => reject(error)
        );
    });
  }

  public loadTariffs(establishment: string): Promise<TPVTariffDTO[]> {
    const request: EstablishmentDTO = {
      id: establishment,
    };
    return new Promise<TPVTariffDTO[]>((resolve, reject) => {
      this.revolvingApi
        .getTariffs(request)
        .toPromise()
        .then(
          (data) => resolve(data),
          (error) => reject(error)
        );
    });
  }

  public sendQuery(purchasesDataForm: FormGroup, userId: string, pagination: string): Promise<TpvQueryResponse> {
    const request: TpvDetailDTO = {
      authorization: '',
      contract: '',
      dateFrom: this.datePipe.transform(purchasesDataForm.get('initialDate')?.value, 'yyyyMMdd'),
      dateTo: this.datePipe.transform(purchasesDataForm.get('finalDate')?.value, 'yyyyMMdd'),
      language: 'es',
      operationType: purchasesDataForm.get('liquidations')?.value,
      printDocumentType: '', //It has no value since we'll call it for the purpose of printing and exporting
      selectedGroupType:
        purchasesDataForm.get('queryType')?.value === TPVTypeSelected.group
          ? purchasesDataForm.get('radioButtonDetail')?.value
          : '',
      selectedNifCifType:
        purchasesDataForm.get('queryType')?.value === TPVTypeSelected.prescriptor
          ? purchasesDataForm.get('radioButtonDetail')?.value
          : '',
      selectedPageId: pagination, //Paginación
      stablishment: purchasesDataForm.get('selectOption')?.value,
      userId: userId?.substring(0, userId.length - 1),
      userType: purchasesDataForm.get('queryType')?.value,
    };
    return new Promise<TpvQueryResponse>((resolve, reject) => {
      this.revolvingApi
        .postTpvQuery(request)
        .toPromise()
        .then(
          (data) => resolve(data),
          (error) => reject(error)
        );
    });
  }

  public printQuery(purchasesDataForm: FormGroup, userId: string, documentType: string): Promise<any> {
    const request: TpvDetailDTO = {
      authorization: '',
      contract: '',
      dateFrom: this.datePipe.transform(purchasesDataForm.get('initialDate')?.value, 'yyyyMMdd'),
      dateTo: this.datePipe.transform(purchasesDataForm.get('finalDate')?.value, 'yyyyMMdd'),
      language: 'es',
      operationType: purchasesDataForm.get('liquidations')?.value,
      printDocumentType: documentType,
      selectedGroupType:
        purchasesDataForm.get('queryType')?.value === TPVTypeSelected.group
          ? purchasesDataForm.get('radioButtonDetail')?.value
          : '',
      selectedNifCifType:
        purchasesDataForm.get('queryType')?.value === TPVTypeSelected.prescriptor
          ? purchasesDataForm.get('radioButtonDetail')?.value
          : '',
      selectedPageId: '',
      stablishment: purchasesDataForm.get('selectOption')?.value,
      userId: userId?.substring(0, userId.length - 1),
      userType: purchasesDataForm.get('queryType')?.value,
    };
    return new Promise<any>((resolve, reject) => {
      this.revolvingApi
        .postTpvQueryPrint(request)
        .toPromise()
        .then(
          (data) => resolve(data),
          (error) => reject(error)
        );
    });
  }

  public printQueryBoleta(operation: TpvDetailOperation, userId: string): Promise<any> {
    const request: TpvBoleta = {
      authorization: operation.strNuRefere,
      contract: operation.strNumCon,
      operationType: operation.strTipOper,
      stablishment: operation.strIdEstablecimiento,
      userId: userId.slice(-1),
    };
    return new Promise<any>((resolve, reject) => {
      this.revolvingApi
        .postTpvQueryPrintBoleta(request)
        .toPromise()
        .then(
          (data) => resolve(data),
          (error) => reject(error)
        );
    });
  }

  public getDescriptionPurchase(refundDataForm: FormGroup): Promise<string> {
    console.log('refundform: ', refundDataForm);
    const request: TpvRefundDTO = {
      establishment: refundDataForm.get('establishment')?.value,
      contractNumber: refundDataForm.get('contractNum')?.value,
      authorization: refundDataForm.get('authorizationNum')?.value,
      amount: '0,00', //refundDataForm.get('refundImport')?.value.replace('€', '').trim(),
      nif: refundDataForm.get('nifNie')?.value,
      description: refundDataForm.get('description')?.value,
    };

    return new Promise<any>((resolve, reject) => {
      this.revolvingApi
        .getPurchaseDescription(request)
        .toPromise()
        .then(
          (data: any) => {
            resolve(data);
          },
          (error) => {
            //Añado esto porque la descripción está llegando en el error
            if (error.status == 200) {
              resolve(error.error.text);
            } else {
              reject(error);
            }
          }
        );
    });
  }
}
